import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send } from 'lucide-react';
import api from '@/lib/api';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MonthlyReportSection({ user, refresh }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => { api.get('/reports/monthly').then(({ data }) => setS(data)).catch(() => setS(false)); }, []);
  const on = user.monthly_report_enabled !== false;

  const toggle = async () => {
    setBusy('toggle');
    try { await api.patch('/me/monthly-report', { enabled: !on }); await refresh(); toast.success(on ? 'Monthly report turned off' : 'Monthly report turned on'); }
    catch { toast.error('Could not update setting'); } finally { setBusy(''); }
  };
  const sendNow = async () => {
    setBusy('send');
    try { const { data } = await api.post('/reports/monthly/send-me'); toast.success(`Report sent to ${data.to}`); }
    catch { toast.error('Could not send the report right now'); } finally { setBusy(''); }
  };

  const tiles = s ? [
    ['Money in', gbp(s.money_in), `${s.payments} payment${s.payments === 1 ? '' : 's'}`],
    ['Win rate', s.win_rate == null ? '—' : `${s.win_rate}%`, `${s.quotes_won} won of ${s.quotes_sent} sent`],
    ['Still owed', gbp(s.owed), `${s.owed_invoices} unpaid invoice${s.owed_invoices === 1 ? '' : 's'}`],
  ] : [];

  return (
    <div className="tm-card p-6 space-y-5" data-testid="settings-monthly-report">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Mail size={20} className="text-brand" /> Monthly report email</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">On the 1st of every month we email <strong>{user.email}</strong> a summary of the month before: money in, how many quotes you won and what customers still owe you.</p>
        </div>
        <button type="button" role="switch" aria-checked={on} onClick={toggle} disabled={busy === 'toggle'} data-testid="monthly-report-toggle"
          className={`relative h-7 w-12 rounded-full transition-colors ${on ? 'bg-brand' : 'bg-slate-300'}`}>
          <span className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      <p className="text-sm font-semibold" data-testid="monthly-report-status">{on ? 'On — next report arrives on the 1st' : 'Off — you won’t get monthly reports'}</p>
      {s && (
        <div>
          <p className="tm-label">Preview · {s.label}</p>
          <div className="grid sm:grid-cols-3 gap-3" data-testid="monthly-report-preview">
            {tiles.map(([l, v, sub]) => (
              <div key={l} className="border border-slate-200 rounded-lg p-4" data-testid={`monthly-tile-${l.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{l}</p>
                <p className={`font-display text-2xl font-semibold mt-1 ${l === 'Still owed' && s.owed > 0 ? 'text-rose-600' : ''}`}>{v}</p>
                <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <button type="button" onClick={sendNow} disabled={busy === 'send'} className="tm-btn" data-testid="monthly-report-send-now">
        <Send size={15} /> {busy === 'send' ? 'Sending…' : 'Email me last month’s report now'}
      </button>
    </div>
  );
}
